import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, History } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { fetchCountryAuditLog, type AuditRow } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A11.22 — Davlat o'zgarishlar jurnali: kim qachon qaysi davlatni ochdi/yopdi.
 *
 * Yangi endpoint YO'Q — mavjud `/admin/audit` ustidan `block_country` /
 * `unblock_country` amallariga filtrlaydi (`fetchCountryAuditLog`). Sabab/four-eyes
 * belgisi `detail` ustunida ko'rinadi.
 */
export function CountryChangeLog() {
  const t = useT()

  const columns = useMemo<AdminColumn<AuditRow>[]>(
    () => [
      { key: 'created_at', header: t.col.time, type: 'date', width: 150 },
      { key: 'admin', header: t.col.staff, type: 'text', width: 160 },
      {
        key: 'action',
        header: t.col.action,
        type: 'enum',
        width: 150,
        options: [
          { value: 'block_country', label: t.geo.block, status: 'banned' },
          { value: 'unblock_country', label: t.geo.unblock, status: 'active' },
        ],
      },
      { key: 'target', header: t.col.country, type: 'id', width: 140 },
      { key: 'detail', header: t.col.detail, type: 'longtext', minWidth: 200 },
      { key: 'ip', header: t.col.ip, type: 'id', width: 150, piiLevel: 'L3' },
    ],
    [t],
  )

  const fetcher = useCallback(fetchCountryAuditLog, [])

  return (
    <>
      <PageHeader
        title={t.geo.changelog}
        screenId="A11.22"
        actions={
          <Link
            to="/geo"
            className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
          >
            <ChevronLeft size={13} />
            {t.nav.geo}
          </Link>
        }
      />
      <DataTable<AuditRow>
        screenId="A11.22"
        permission={PERMISSIONS.COUNTRIES_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id ?? `${r.created_at}:${r.target ?? ''}`)}
        exportable
        emptyState={{ icon: <History size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
